import { useRef, useEffect } from 'react';
import styles from './index.module.scss';

interface MarqueeProps {
    items: React.ReactNode[];
    speed?: number;
    direction?: 'left' | 'right';
    pauseOnHover?: boolean;
    className?: string;
    gap?: number;
}

export default function Marquee({ items, speed = 0.5, direction = 'left', pauseOnHover = true, className = '', gap = 24 }: MarqueeProps) {
    const trackRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const track = trackRef.current;
        if (!track) return;

        let animationId: number;
        const firstSetWidth = track.scrollWidth / 2;
        let position = direction === 'right' ? -firstSetWidth : 0;

        const animate = () => {
            if (direction === 'left') {
                position -= speed;
                if (Math.abs(position) >= firstSetWidth) {
                    position = 0;
                }
            } else {
                position += speed;
                if (position >= 0) {
                    position = -firstSetWidth;
                }
            }
            track.style.transform = `translateX(${position}px)`;
            animationId = requestAnimationFrame(animate);
        };

        animationId = requestAnimationFrame(animate);

        const handleMouseEnter = () => cancelAnimationFrame(animationId);
        const handleMouseLeave = () => {
            animationId = requestAnimationFrame(animate);
        };

        if (pauseOnHover) {
            track.addEventListener('mouseenter', handleMouseEnter);
            track.addEventListener('mouseleave', handleMouseLeave);
        }

        return () => {
            cancelAnimationFrame(animationId);
            if (pauseOnHover) {
                track.removeEventListener('mouseenter', handleMouseEnter);
                track.removeEventListener('mouseleave', handleMouseLeave);
            }
        };
    }, [speed, direction, pauseOnHover]);

    const allItems = [...items, ...items];

    return (
        <div className={`${styles.Marquee} ${className}`}>
            <div className="marquee-track" ref={trackRef} style={{ gap: `${gap}px` }}>
                {allItems.map((item, index) => (
                    <div key={index} className="marquee-item">
                        {item}
                    </div>
                ))}
            </div>
        </div>
    );
}
